import { useEffect, useRef, useState } from "react";

import TabPanel, { TabPanelProps } from "@mui/lab/TabPanel";
import { DatePicker, DatePickerProps } from "@mui/x-date-pickers/DatePicker";
import {} from "@mui/x-charts/LineChart";
import { Box, Stack, Toolbar, Typography, useTheme } from "@mui/material";

import DatasetLegend from "./DatasetLegend";
import { ChartFineTuneButton } from "./ChartFineTuneButton";
import { HorizontalStack, VerticalStack } from "./primitives/Stacks";
import { Patient } from "../models/patient";
import { Spacer } from "./primitives/Spacer";
import { useAppContext } from "../context/AppContext";
import { useVisualisationData } from "../hooks/useVisualisationData";
import Chart from "./Chart";
import { useTranslation } from "react-i18next";

const NAME = "ChartTab";
const chartClasses = {
  highlighted: `${NAME}-highlighted`,
  dimmed: `${NAME}-dimmed`,
};

export const ChartTab: React.FC<TabPanelProps & { patient: Patient }> = ({
  patient,
  ...tabPanelProps
}) => {
  const svgRef = useRef<SVGSVGElement>(null);

  const [timeframe, setTimeframe] = useState<{
    from?: Date | null;
    to?: Date | null;
  }>({ from: null, to: new Date() });
  const [showNight, setShowNight] = useState(true);
  const [showMidnight, setShowMidnight] = useState(true);
  const [extrasAvailable, setExtrasAvailable] = useState(false);

  const { dataConfig } = useAppContext();

  const { series } = useVisualisationData({
    exportId: dataConfig.exportId || undefined,
    patientId: patient.id,
    timeframe,
  });

  const { t } = useTranslation();

  const theme = useTheme();

  const onChangeTimeframe = (timeframe: {
    from?: Date | null;
    to?: Date | null;
  }) => {
    setTimeframe((s) => {
      // Clone the current state to avoid direct mutation
      let newState = { ...s };

      // Update newState with the new timeframe values
      newState = { ...newState, ...timeframe };

      // Check if both from and to are not null
      if (newState.from && newState.to) {
        // Convert both dates to comparable values
        const fromTime = newState.from.getTime();
        const toTime = newState.to.getTime();

        // If from is after to, set to to the next day of from
        if (fromTime > toTime) {
          newState.to = new Date(fromTime);
          newState.to.setDate(newState.to.getDate() + 1);
        }
        // If to is before from, set from to the day before to
        else if (toTime < fromTime) {
          newState.from = new Date(toTime);
          newState.from.setDate(newState.from.getDate() - 1);
        }
      }

      // Return the updated state
      return newState;
    });
  };

  const dateFieldProps: DatePickerProps<Date> = {
    disableFuture: true,
    slotProps: {
      textField: {
        InputProps: { sx: { pr: 2 } },
        fullWidth: false,
        size: "small",
      },
    },
  };

  const handleMouseMove = (event: MouseEvent) => {
    const svgEl = svgRef.current;
    if (!svgEl) return;

    const svgPaths = svgEl.querySelectorAll<SVGPathElement>(
      "path.MuiLineElement-root"
    );
    let closestPath: SVGPathElement | null = null;

    const createSVGPoint = (x: number, y: number) => {
      const point = svgEl.createSVGPoint();
      point.x = x;
      point.y = y;
      return point;
    };

    const point = svgEl.createSVGPoint();
    point.x = event.clientX;
    point.y = event.clientY;

    const transformedPoint = point.matrixTransform(
      svgEl.getScreenCTM()?.inverse()
    );

    // Create a bounding box around the point with a 3px offset
    const offset = 5;
    const bbox = {
      x: transformedPoint.x - offset,
      y: transformedPoint.y - offset,
      width: offset * 2,
      height: offset * 2,
    };

    const transformedPointSVG = createSVGPoint(
      transformedPoint.x,
      transformedPoint.y
    );
    const bboxPoints = [
      createSVGPoint(bbox.x, bbox.y),
      createSVGPoint(bbox.x + bbox.width, bbox.y),
      createSVGPoint(bbox.x, bbox.y + bbox.height),
      createSVGPoint(bbox.x + bbox.width, bbox.y + bbox.height),
    ];

    svgPaths.forEach((path) => {
      const isInPath =
        path.isPointInStroke(transformedPointSVG) ||
        bboxPoints.some((point) => path.isPointInStroke(point));

      if (isInPath) {
        closestPath = path;
      }
    });

    if (!closestPath) {
      svgPaths.forEach((path) => {
        path.classList.remove(chartClasses.highlighted);
        path.classList.remove(chartClasses.dimmed);
      });
    } else {
      svgPaths.forEach((path) => {
        path.classList.add(chartClasses.dimmed);
        path.classList.remove(chartClasses.highlighted);
      });
      (closestPath as SVGPathElement).classList.add(chartClasses.highlighted);
      (closestPath as SVGPathElement).classList.remove(chartClasses.dimmed);
    }
  };

  useEffect(() => {
    const svgEl = svgRef.current;
    if (svgEl) {
      svgEl.addEventListener("mousemove", handleMouseMove);
    }

    return () => {
      if (svgEl) {
        svgEl.removeEventListener("mousemove", handleMouseMove);
      }
    };
  }, []);

  return (
    <TabPanel {...tabPanelProps}>
      <VerticalStack>
        <Toolbar disableGutters>
          <ChartFineTuneButton
            onChange={onChangeTimeframe}
            showNight={showNight}
            setShowNight={setShowNight}
            showMidnight={showMidnight}
            setShowMidnight={setShowMidnight}
          />
          <HorizontalStack
            alignItems="center"
            flexGrow={1}
            spacing={1}
            justifyContent="flex-end"
            sx={{ ml: 2 }}
          >
            <DatePicker
              onChange={(newValue) => onChangeTimeframe({ from: newValue })}
              value={timeframe.from}
              {...dateFieldProps}
            />
            <span>–</span>
            <DatePicker
              onChange={(newValue) => onChangeTimeframe({ to: newValue })}
              value={timeframe.to}
              {...dateFieldProps}
            />
          </HorizontalStack>
        </Toolbar>
        <Box position="relative" height={400} width="100%">
          {(showMidnight || showNight) && (
            <Stack
              direction="row"
              flexWrap="wrap"
              mr={1}
              justifySelf="flex-end"
            >
              {showNight && (
                <HorizontalStack spacing={1} alignItems="center">
                  <Box
                    width={theme.spacing(1.5)}
                    height={theme.spacing(1.5)}
                    bgcolor={theme.palette.grey[300]}
                    borderRadius={0.5}
                  ></Box>
                  <Typography color="text.secondary" variant="body2">
                    22:00 - 6:00
                  </Typography>
                </HorizontalStack>
              )}
              {showMidnight && (
                <HorizontalStack spacing={1.5} alignItems="center">
                  <Box
                    width={theme.spacing(0.1)}
                    height={theme.spacing(2.5)}
                    bgcolor={theme.palette.grey[400]}
                    borderRadius={0.5}
                  ></Box>
                  <Typography color="text.secondary" variant="body2">
                    {t("tabs.chart.midnight")}
                  </Typography>
                </HorizontalStack>
              )}
            </Stack>
          )}
          <Box zIndex={1} height="100%" width="100%">
            <Chart
              timeframe={timeframe}
              patient={patient}
              showNight={showNight}
              showMidnight={showMidnight}
              setExtrasAvailable={setExtrasAvailable}
            />
          </Box>
        </Box>
      </VerticalStack>
      <Spacer />
      <DatasetLegend
        series={series}
        sx={({ spacing }) => ({
          bottom: spacing(2),
          position: "sticky",
          zIndex: 1,
        })}
        isExtrasVisible={extrasAvailable}
      />
    </TabPanel>
  );
};

export default ChartTab;
